import { Timestamp, doc, getDoc, setDoc } from 'firebase/firestore';
import { getFirebase, isFirebaseConfigured } from '@/config/firebase';
import { STORAGE_KEYS } from '@/config/constants';

export interface UserProfile {
  uid: string;
  nickname: string;
  bestScore: number;
  bestLevel: number;
  totalGames: number;
  isAnonymous: boolean;
  lastPlayedAt: number;
}

const COLLECTION = 'users';

const LOCAL_PROFILE_KEY = 'mn:profile';

function loadLocal(): UserProfile | null {
  try {
    const raw = localStorage.getItem(LOCAL_PROFILE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UserProfile;
  } catch {
    return null;
  }
}

function saveLocal(p: UserProfile): void {
  localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(p));
  if (p.bestScore > 0) {
    localStorage.setItem(STORAGE_KEYS.bestScore, String(p.bestScore));
  }
  if (p.nickname) {
    localStorage.setItem(STORAGE_KEYS.nickname, p.nickname);
  }
}

export const userProfileService = {
  async load(uid: string): Promise<UserProfile | null> {
    const localFallback = (): UserProfile | null => {
      const local = loadLocal();
      return local && local.uid === uid ? local : null;
    };

    if (!isFirebaseConfigured()) return localFallback();

    const { db } = getFirebase();
    try {
      const ref = doc(db, COLLECTION, uid);
      // Timeout para no quedarnos colgados si las rules deniegan o no hay red.
      const snap = await Promise.race([
        getDoc(ref),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('firestore load timeout')), 4000)
        )
      ]);
      if (!snap.exists()) return localFallback();
      const data = snap.data();
      const profile: UserProfile = {
        uid,
        nickname: data.nickname ?? '',
        bestScore: data.bestScore ?? 0,
        bestLevel: data.bestLevel ?? 0,
        totalGames: data.totalGames ?? 0,
        isAnonymous: data.isAnonymous ?? true,
        lastPlayedAt: (data.lastPlayedAt as Timestamp | undefined)?.toMillis() ?? 0
      };
      saveLocal(profile);
      return profile;
    } catch (err) {
      console.error('profile load failed', err);
      return localFallback();
    }
  },

  async setNickname(uid: string, nickname: string, isAnonymous: boolean): Promise<boolean> {
    const trimmed = nickname.trim().slice(0, 20);
    if (!trimmed) return true;

    const existing = loadLocal() ?? {
      uid,
      nickname: trimmed,
      bestScore: 0,
      bestLevel: 0,
      totalGames: 0,
      isAnonymous,
      lastPlayedAt: Date.now()
    };
    existing.uid = uid;
    existing.nickname = trimmed;
    existing.isAnonymous = isAnonymous;
    saveLocal(existing);

    if (!isFirebaseConfigured()) return true;
    const { db } = getFirebase();
    try {
      const ref = doc(db, COLLECTION, uid);
      // Sin pre-read: si las rules permiten escribir pero no leer, igual funciona.
      await setDoc(
        ref,
        {
          nickname: trimmed,
          isAnonymous,
          lastPlayedAt: Timestamp.now()
        },
        { merge: true }
      );
      return true;
    } catch (err) {
      console.error('setNickname failed', err);
      return false;
    }
  },

  async recordGame(
    uid: string,
    nickname: string,
    isAnonymous: boolean,
    score: number,
    level: number
  ): Promise<{ profile: UserProfile; isNewRecord: boolean; cloudSynced: boolean }> {
    // Source of truth para el estado previo: localStorage. Evitamos getDoc()
    // para no caer si las rules deniegan reads.
    const local = loadLocal();
    const existing: UserProfile =
      local && local.uid === uid
        ? local
        : {
            uid,
            nickname,
            bestScore: 0,
            bestLevel: 0,
            totalGames: 0,
            isAnonymous,
            lastPlayedAt: Date.now()
          };

    const isNewRecord = score > existing.bestScore;
    const updated: UserProfile = {
      ...existing,
      uid,
      nickname: nickname || existing.nickname,
      isAnonymous,
      bestScore: Math.max(existing.bestScore, score),
      bestLevel: Math.max(existing.bestLevel, level),
      totalGames: existing.totalGames + 1,
      lastPlayedAt: Date.now()
    };
    saveLocal(updated);

    let cloudSynced = false;
    if (isFirebaseConfigured()) {
      const { db } = getFirebase();
      try {
        const ref = doc(db, COLLECTION, uid);
        await setDoc(
          ref,
          {
            nickname: updated.nickname,
            isAnonymous: updated.isAnonymous,
            bestScore: updated.bestScore,
            bestLevel: updated.bestLevel,
            totalGames: updated.totalGames,
            lastPlayedAt: Timestamp.now()
          },
          { merge: true }
        );
        cloudSynced = true;
      } catch (err) {
        console.error('recordGame failed', err);
      }
    }

    return { profile: updated, isNewRecord, cloudSynced };
  },

  /**
   * Copia el perfil de un uid local al uid de Google. Si el destino ya tiene
   * récord más alto, se conserva el mejor de ambos.
   */
  async migrateLocalToGoogle(
    fromUid: string | null,
    toUid: string,
    nickname: string
  ): Promise<void> {
    const trimmed = nickname.trim().slice(0, 20);
    const src = fromUid ? loadLocal() : null;
    const srcProfile = src && fromUid && src.uid === fromUid ? src : null;

    const merged: UserProfile = {
      uid: toUid,
      nickname: trimmed || srcProfile?.nickname || '',
      bestScore: srcProfile?.bestScore ?? 0,
      bestLevel: srcProfile?.bestLevel ?? 0,
      totalGames: srcProfile?.totalGames ?? 0,
      isAnonymous: false,
      lastPlayedAt: Date.now()
    };

    if (isFirebaseConfigured()) {
      const { db } = getFirebase();
      try {
        const destRef = doc(db, COLLECTION, toUid);
        const destSnap = await getDoc(destRef);
        if (destSnap.exists()) {
          const data = destSnap.data();
          merged.bestScore = Math.max(merged.bestScore, data.bestScore ?? 0);
          merged.bestLevel = Math.max(merged.bestLevel, data.bestLevel ?? 0);
          merged.totalGames = (data.totalGames ?? 0) + merged.totalGames;
          merged.nickname = merged.nickname || data.nickname || '';
        }
        await setDoc(
          destRef,
          {
            nickname: merged.nickname,
            isAnonymous: false,
            bestScore: merged.bestScore,
            bestLevel: merged.bestLevel,
            totalGames: merged.totalGames,
            lastPlayedAt: Timestamp.now()
          },
          { merge: true }
        );
      } catch (err) {
        console.error('migrate firestore failed', err);
      }
    }

    saveLocal(merged);
  }
};
